import { Text, VStack } from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { DashboardData } from '../../../types/analyticsMap'
import { ChartFrame, ChartLegend, chartTheme, seriesColor, vizDiv, vizPartyVar, vizSeqVar } from '../../shared/viz'

// =====================================================================
// CHART DATA HELPERS
// =====================================================================

function toChartData(record: Record<string, number> | undefined): { name: string; value: number }[] {
  if (!record) return []
  return Object.entries(record).map(([name, value]) => ({ name, value }))
}

function sortedNumericData(record: Record<string, number> | undefined): { name: string; value: number }[] {
  if (!record) return []
  return Object.entries(record)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => {
      const numA = parseFloat(a.name)
      const numB = parseFloat(b.name)
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB
      return a.name.localeCompare(b.name)
    })
}

// =====================================================================
// COLOR HELPERS (A6 slice 9: viz tokens, not raw hex — party/race/ideology/
// turnout/age all resolve through the shared viz layer so they flip with
// light/dark mode. See frontend/src/components/shared/viz.)
// =====================================================================

const SEQ_STEPS = [100, 200, 300, 400, 500, 600, 700] as const

/** Ordinal-magnitude color: maps a bucket's position within an ordered series
 *  to the nearest of the 7 sequential steps. Used for turnout AND age buckets.
 *  Exported for direct unit testing — recharts Cell fills aren't observable
 *  through the DOM under jsdom (no SVG shape geometry), so the mapping logic
 *  itself is the testable seam. */
export function seqColorForBucket(index: number, total: number): string {
  const t = total > 1 ? index / (total - 1) : 0.5
  const stepIndex = Math.min(SEQ_STEPS.length - 1, Math.floor(t * SEQ_STEPS.length))
  return vizSeqVar(SEQ_STEPS[stepIndex])
}

// Ideology convention (confirmed): 0 = most conservative, 100 = most progressive.
// Diverging: low third -> viz.div.neg (conservative), middle third -> viz.div.mid,
// high third -> viz.div.pos (progressive). Matches the diverging poles used for
// partisan lean/margin elsewhere in the viz layer.
export function getIdeologyColor(index: number, total: number): string {
  const t = total > 1 ? index / (total - 1) : 0.5
  if (t < 1 / 3) return vizDiv.neg
  if (t < 2 / 3) return vizDiv.mid
  return vizDiv.pos
}

// =====================================================================
// INDIVIDUAL CHART COMPONENTS
// =====================================================================

// Shared bar chart for the numeric-bucket distributions (Ideology, Turnout).
// Labeled X/Y axes + a thousands-separated "N contacts" tooltip.
export function NumericBarChart({
  chartData,
  xLabel,
  colorFn,
  tickFormatter,
}: {
  chartData: { name: string; value: number }[]
  xLabel: string
  colorFn: (index: number, total: number) => string
  tickFormatter?: (value: string) => string
}) {
  return (
    <ChartFrame ariaLabel={xLabel} height={180}>
      {/* positive left margin + YAxis width so the axis titles aren't clipped */}
      <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 18, left: 4 }}>
        <XAxis
          dataKey="name"
          tick={{ ...chartTheme.axis.tick, fontSize: 10 }}
          stroke={chartTheme.axis.stroke}
          tickFormatter={tickFormatter}
          label={{ value: xLabel, position: 'insideBottom', offset: -12, style: { fontSize: 10 } }}
        />
        <YAxis
          tick={{ ...chartTheme.axis.tick, fontSize: 10 }}
          stroke={chartTheme.axis.stroke}
          width={44}
          label={{ value: 'Contacts', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }}
        />
        <Tooltip
          separator=""
          formatter={(value) => [`${Number(value).toLocaleString()} contacts`, '']}
          contentStyle={chartTheme.tooltip.contentStyle}
        />
        <Bar dataKey="value" radius={[2, 2, 0, 0]}>
          {chartData.map((_, i) => (
            <Cell key={i} fill={colorFn(i, chartData.length)} />
          ))}
        </Bar>
      </BarChart>
    </ChartFrame>
  )
}

export function IdeologyChart({ data }: { data: DashboardData }) {
  const chartData = sortedNumericData(data.ideologyDistribution)
  if (chartData.length === 0) return <EmptyChart />
  // Ideology polarity confirmed by product owner: 0 = conservative, 100 = progressive.
  return (
    <NumericBarChart
      chartData={chartData}
      xLabel="Ideology score (0 = conservative · 100 = progressive)"
      colorFn={getIdeologyColor}
    />
  )
}

export function PartyChart({ data }: { data: DashboardData }) {
  const chartData = toChartData(data.partyBreakdown)
  if (chartData.length === 0) return <EmptyChart />
  return (
    <VStack align="stretch" gap={2}>
      <ChartFrame ariaLabel="Party breakdown" height={160}>
        <PieChart>
          <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={30} outerRadius={60}>
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={vizPartyVar(entry.name)} />
            ))}
          </Pie>
          <Tooltip contentStyle={chartTheme.tooltip.contentStyle} />
        </PieChart>
      </ChartFrame>
      <ChartLegend variant="categorical" items={chartData.map((d) => ({ label: d.name, colorVar: vizPartyVar(d.name) }))} />
    </VStack>
  )
}

export function RaceChart({ data }: { data: DashboardData }) {
  const chartData = toChartData(data.raceBreakdown)
  if (chartData.length === 0) return <EmptyChart />
  return (
    <VStack align="stretch" gap={2}>
      <ChartFrame ariaLabel="Race breakdown" height={160}>
        <PieChart>
          <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={30} outerRadius={60}>
            {chartData.map((_, i) => (
              <Cell key={i} fill={seriesColor(i)} />
            ))}
          </Pie>
          <Tooltip contentStyle={chartTheme.tooltip.contentStyle} />
        </PieChart>
      </ChartFrame>
      <ChartLegend variant="categorical" items={chartData.map((d, i) => ({ label: d.name, colorVar: seriesColor(i) }))} />
    </VStack>
  )
}

export function AgeChart({ data }: { data: DashboardData }) {
  const chartData = sortedNumericData(data.ageDistribution)
  if (chartData.length === 0) return <EmptyChart />
  return (
    <ChartFrame ariaLabel="Age distribution" height={160}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 20 }}>
        <XAxis type="number" tick={{ ...chartTheme.axis.tick, fontSize: 10 }} stroke={chartTheme.axis.stroke} />
        <YAxis dataKey="name" type="category" tick={{ ...chartTheme.axis.tick, fontSize: 10 }} stroke={chartTheme.axis.stroke} width={50} />
        <Tooltip contentStyle={chartTheme.tooltip.contentStyle} />
        <Bar dataKey="value" radius={[0, 2, 2, 0]}>
          {chartData.map((_, i) => (
            <Cell key={i} fill={seqColorForBucket(i, chartData.length)} />
          ))}
        </Bar>
      </BarChart>
    </ChartFrame>
  )
}

export function TurnoutChart({ data }: { data: DashboardData }) {
  const chartData = sortedNumericData(data.turnoutDistribution)
  if (chartData.length === 0) return <EmptyChart />
  return (
    <NumericBarChart
      chartData={chartData}
      xLabel="Predicted turnout likelihood"
      colorFn={seqColorForBucket}
      tickFormatter={(value) => `${value}%`}
    />
  )
}

export function EmptyChart() {
  return (
    <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>
      No data available
    </Text>
  )
}
